import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";

import { BarChart, LineChart } from "echarts/charts";
import type { BarSeriesOption, LineSeriesOption } from "echarts/charts";
import { AriaComponent, DataZoomComponent, GridComponent, LegendComponent, MarkLineComponent, TooltipComponent } from "echarts/components";
import type { MarkLineComponentOption } from "echarts/components";
import * as echarts from "echarts/core";
import type { EChartsCoreOption } from "echarts/core";
import { SVGRenderer } from "echarts/renderers";

import type { Dataset, MeasurementPanel, PanelData } from "./api";
import { registerPanelChartExporter, type PanelChartExportSize } from "./chartReportExport";
import { chartTimeDayMarkers } from "./chartTimeTicks";
import { chartValueScale } from "./chartValueScale";
import type { NormalizedChartData } from "./dashboardChartData";
import { displayLocale, formatNumber } from "./localization";
import { measurementAxisFormatter, measurementAxisValues, usesDailyMeasurementAxis } from "./measurementChartAxis";
import { runtimeDimensionValueLabel } from "./panelFilters";
import { vehicleClassColor, vehicleClassLabel } from "./vehicleClasses";

echarts.use([
  AriaComponent,
  BarChart,
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  MarkLineComponent,
  SVGRenderer,
  TooltipComponent,
]);

const defaultDashboardTimezone = "Europe/Vienna";
const minimumZoomBuckets = 12;
const seriesColors = ["#2e6684", "#6c8796", "#846b55", "#64755a", "#7b6687"];

function reportChartSVG(option: EChartsCoreOption, size: PanelChartExportSize) {
  const source = option as Record<string, unknown>;
  const configuredZoom = Array.isArray(source.dataZoom) ? source.dataZoom : source.dataZoom ? [source.dataZoom] : [];
  const pixelsPerMillimetre = Math.max(4, 280 / size.width, 150 / size.height);
  const width = Math.round(size.width * pixelsPerMillimetre);
  const height = Math.round(size.height * pixelsPerMillimetre);
  const legend = source.legend && typeof source.legend === "object" && !Array.isArray(source.legend)
    ? { ...(source.legend as Record<string, unknown>), left: 42, right: 8, itemWidth: 12, itemHeight: 3, textStyle: { color: "#707070", fontFamily: "Helvetica, Arial, sans-serif", fontSize: 9 } }
    : source.legend;
  const grid = source.grid && typeof source.grid === "object" && !Array.isArray(source.grid)
    ? { ...(source.grid as Record<string, unknown>), top: legend && (legend as { show?: boolean }).show !== false ? 34 : 15, right: 10, bottom: 28, left: 48 }
    : source.grid;
  const exportOption = {
    ...source,
    animation: false,
    grid,
    legend,
    tooltip: { show: false },
    dataZoom: configuredZoom.map((entry) => ({ ...(entry as Record<string, unknown>), start: 0, end: 100, show: false })),
  } as EChartsCoreOption;
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  Object.assign(host.style, { position: "fixed", left: "-10000px", top: "0", width: `${width}px`, height: `${height}px`, pointerEvents: "none" });
  document.body.append(host);
  const chart = echarts.init(host, undefined, { renderer: "svg", width, height });
  try {
    chart.setOption(exportOption, { notMerge: true, lazyUpdate: false });
    const svg = host.querySelector("svg")?.cloneNode(true) as SVGSVGElement | undefined;
    svg?.setAttribute("viewBox", `0 0 ${width} ${height}`);
    svg?.setAttribute("preserveAspectRatio", "xMidYMid meet");
    svg?.querySelectorAll("text").forEach((element) => {
      element.setAttribute("font-family", "Helvetica, Arial, sans-serif");
      element.style.fontFamily = "Helvetica, Arial, sans-serif";
    });
    return svg ?? null;
  } finally {
    chart.dispose();
    host.remove();
  }
}

type ChartKind = "bar" | "line";

function valueLabel(value: number, format = "", unit = "") {
  const formatted = format === "percent"
    ? `${formatNumber(value, { maximumFractionDigits: 1 })} %`
    : formatNumber(value, { maximumFractionDigits: 2 });
  return unit && format !== "percent" ? `${formatted} ${unit}` : formatted;
}

function groupLabel(groups: Record<string, unknown>, useVehicleClassLabels: boolean, dataset?: Dataset) {
  const values = Object.entries(groups).map(([name, value]) => useVehicleClassLabels && name === "class" ? vehicleClassLabel(value) : runtimeDimensionValueLabel(dataset, name, value));
  return values.length ? values.join(" · ") : "Gesamt";
}

function seriesColor(groups: Record<string, unknown>, index: number, useVehicleClassColors: boolean) {
  return useVehicleClassColors && Object.hasOwn(groups, "class") ? vehicleClassColor(groups.class) : seriesColors[index % seriesColors.length];
}

function tooltipFormatter(timeZone: string, format: string | undefined, unit: string) {
  const timestampFormatter = new Intl.DateTimeFormat(displayLocale, { dateStyle: "short", timeStyle: "short", timeZone });
  return (parameters: unknown) => {
    const entries = (Array.isArray(parameters) ? parameters : [parameters]).filter((entry): entry is { axisValue?: unknown; seriesName?: string; value?: unknown } => Boolean(entry) && typeof entry === "object");
    const firstValue = entries[0]?.value;
    const timestamp = Array.isArray(firstValue) ? Number(firstValue[0]) : Number(entries[0]?.axisValue);
    const lines = Number.isFinite(timestamp) ? [timestampFormatter.format(timestamp)] : [];
    const values: number[] = [];
    for (const entry of entries) {
      const value = Array.isArray(entry.value) ? entry.value[1] : entry.value;
      if (value === null || value === undefined) continue;
      const rawValue = Number(value);
      if (Number.isFinite(rawValue)) {
        values.push(rawValue);
        lines.push(`${entry.seriesName ?? "Wert"}: ${valueLabel(rawValue, format, unit)}`);
      }
    }
    if (values.length > 0) lines.push("", `Summe: ${valueLabel(values.reduce((total, value) => total + value, 0), format, unit)}`);
    return lines.join("\n");
  };
}

type EChartsCanvasHandle = {
  resetZoom: () => void;
};

const EChartsCanvas = forwardRef<EChartsCanvasHandle, {
  label: string;
  option: EChartsCoreOption;
  zoomEnabled: boolean;
  onZoomChange: (zoomed: boolean) => void;
}>(function EChartsCanvas({ label, option, zoomEnabled, onZoomChange }, ref) {
  const container = useRef<HTMLDivElement>(null);
  const instance = useRef<ReturnType<typeof echarts.init> | null>(null);

  useImperativeHandle(ref, () => ({
    resetZoom() {
      const chart = instance.current;
      if (!chart) return;
      chart.dispatchAction({ type: "dataZoom", dataZoomIndex: 0, start: 0, end: 100 });
      if (container.current) {
        container.current.dataset.zoomStart = "0";
        container.current.dataset.zoomEnd = "100";
      }
      onZoomChange(false);
    },
  }), [onZoomChange]);

  useEffect(() => {
    const element = container.current;
    if (!element) return;
    const chart = echarts.init(element, undefined, { renderer: "svg" });
    instance.current = chart;
    chart.on("datazoom", (event) => {
      const payload = event as { start?: number; end?: number; batch?: Array<{ start?: number; end?: number }> };
      const range = payload.batch?.[0] ?? payload;
      if (typeof range.start === "number") element.dataset.zoomStart = String(range.start);
      if (typeof range.end === "number") element.dataset.zoomEnd = String(range.end);
      onZoomChange((range.start ?? 0) > 0 || (range.end ?? 100) < 100);
    });
    const resize = () => chart.resize();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(resize);
    observer?.observe(element);
    if (!observer) window.addEventListener("resize", resize);
    return () => {
      observer?.disconnect();
      if (!observer) window.removeEventListener("resize", resize);
      instance.current = null;
      chart.dispose();
    };
  }, [onZoomChange]);

  useEffect(() => {
    instance.current?.setOption(option, { notMerge: true });
    onZoomChange(false);
  }, [onZoomChange, option]);

  return <div ref={container} className="measurement-echart-canvas" role="img" aria-label={label} data-zoom-start={zoomEnabled ? "0" : undefined} data-zoom-end={zoomEnabled ? "100" : undefined} />;
});

export function MeasurementPanelChart({ kind, panel, data, chart, unit, useVehicleClassColors, dataset }: {
  kind: ChartKind;
  panel: MeasurementPanel;
  data: PanelData;
  chart: NormalizedChartData;
  unit: string;
  useVehicleClassColors: boolean;
  dataset?: Dataset;
}) {
  const timeZone = data.timezone ?? defaultDashboardTimezone;
  const dayMarkers = useMemo(() => chartTimeDayMarkers(chart.rangeStart, chart.rangeEnd, timeZone), [chart.rangeEnd, chart.rangeStart, timeZone]);
  const dailyAxis = usesDailyMeasurementAxis(chart.bucketSeconds);
  const dailyAxisValues = useMemo(() => measurementAxisValues(chart), [chart]);
  const bucketCount = Math.max(0, ...chart.series.map((series) => series.points.length));
  const zoomConfigured = panel.type === "histogram" || (panel.show_zoom_slider ?? (panel.type === "timeseries"));
  const showZoomSlider = zoomConfigured && bucketCount >= minimumZoomBuckets;
  const [zoomed, setZoomed] = useState(false);
  const chartCanvas = useRef<EChartsCanvasHandle>(null);
  const gapCount = chart.series.reduce((total, series) => total + series.points.filter((point) => point.value === null).length, 0);
  const zeroValueCount = chart.series.reduce((total, series) => total + series.points.filter((point) => point.value === 0).length, 0);
  const valueScale = useMemo(() => {
    const values = chart.series.flatMap((series) => series.points.map((point) => point.value).filter((value): value is number => value !== null));
    const totals = Array.from({ length: bucketCount }, (_, index) => chart.series.reduce((total, series) => total + (series.points[index]?.value ?? 0), 0));
    return kind === "bar"
      ? chartValueScale(0, totals.reduce((maximum, value) => Math.max(maximum, value), 0), 4, useVehicleClassColors)
      : chartValueScale(values.reduce((minimum, value) => Math.min(minimum, value), 0), values.reduce((maximum, value) => Math.max(maximum, value), 0), 4, useVehicleClassColors);
  }, [bucketCount, chart.series, kind, useVehicleClassColors]);
  const currentFormatter = useMemo(() => new Intl.DateTimeFormat(displayLocale, { dateStyle: "short", timeStyle: "short", timeZone }), [timeZone]);
  const axisFormatter = useMemo(() => measurementAxisFormatter(timeZone, chart.bucketSeconds, chart.rangeEnd - chart.rangeStart), [chart.bucketSeconds, chart.rangeEnd, chart.rangeStart, timeZone]);
  const option = useMemo<EChartsCoreOption>(() => {
    const reducedMotion = typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    return {
      animation: !reducedMotion,
      animationDuration: 250,
      aria: { enabled: true, decal: { show: false } },
      color: chart.series.map((series, index) => seriesColor(series.groups, index, useVehicleClassColors)),
      grid: {
        top: chart.series.length > 1 ? 42 : 18,
        right: 16,
        bottom: showZoomSlider ? 72 : 34,
        left: 58,
        containLabel: false,
      },
      legend: chart.series.length > 1 ? {
        top: 0,
        left: 54,
        right: 12,
        itemWidth: 14,
        itemHeight: 3,
        textStyle: { color: "#707070", fontFamily: "IBM Plex Sans", fontSize: 10 },
      } : { show: false },
      tooltip: {
        trigger: "axis",
        renderMode: "richText",
        confine: true,
        formatter: tooltipFormatter(timeZone, panel.format, unit),
        axisPointer: {
          type: "cross",
          crossStyle: { color: "#707070", width: 1, type: "dashed" },
          label: { color: "#ffffff", backgroundColor: "#2e6684", fontFamily: "IBM Plex Sans", fontSize: 10 },
        },
        textStyle: { fontFamily: "IBM Plex Sans", fontSize: 11 },
      },
      xAxis: {
        type: dailyAxis ? "category" : "time",
        ...(dailyAxis ? { data: dailyAxisValues, boundaryGap: kind === "bar" } : { min: chart.rangeStart, max: chart.rangeEnd, boundaryGap: false }),
        axisLine: { lineStyle: { color: "#cfd2d6" } },
        axisTick: { show: false },
        axisLabel: {
          show: true,
          color: "#949a9e",
          fontFamily: "IBM Plex Sans",
          fontSize: 9,
          hideOverlap: true,
          formatter: (value: string | number) => axisFormatter(Number(value)),
        },
        axisPointer: { label: { formatter: ({ value }: { value: string | number }) => axisFormatter(Number(value)) } },
        splitLine: { show: true, lineStyle: { color: "#e4e7e9", type: "dashed" } },
      },
      yAxis: {
        type: "value",
        min: valueScale.minimum,
        max: valueScale.maximum,
        interval: valueScale.ticks.length > 1 ? valueScale.ticks[1].value - valueScale.ticks[0].value : undefined,
        name: panel.format === "percent" ? "%" : unit || "Anzahl",
        nameLocation: "end",
        nameGap: 8,
        nameTextStyle: { align: "left", color: "#707070", fontFamily: "IBM Plex Sans", fontSize: 8 },
        axisLine: { show: true, lineStyle: { color: "#cfd2d6" } },
        axisTick: { show: false },
        axisLabel: { show: true, color: "#949a9e", fontFamily: "IBM Plex Sans", fontSize: 9, formatter: (value: number) => formatNumber(value, { maximumFractionDigits: 2 }) },
        splitLine: { show: true, lineStyle: { color: "#e4e7e9" } },
      },
      dataZoom: showZoomSlider ? [{
        type: "slider",
        show: true,
        xAxisIndex: 0,
        start: 0,
        end: 100,
        bottom: 8,
        height: 28,
        filterMode: "none",
        showDetail: true,
        showDataShadow: true,
        borderColor: "#cfd2d6",
        backgroundColor: "#f3f3f3",
        fillerColor: "rgba(56, 124, 160, 0.16)",
        dataBackground: { lineStyle: { color: "#949a9e" }, areaStyle: { color: "rgba(148, 154, 158, 0.12)" } },
        selectedDataBackground: { lineStyle: { color: "#387ca0" }, areaStyle: { color: "rgba(56, 124, 160, 0.16)" } },
        handleStyle: { color: "#ffffff", borderColor: "#387ca0" },
        moveHandleStyle: { color: "#387ca0", opacity: 0.4 },
        textStyle: { color: "#707070", fontFamily: "IBM Plex Sans", fontSize: 9 },
        labelFormatter: (value: string | number, valueStr: string) => {
          const numericValue = Number(value);
          const timestamp = dailyAxis && Number.isInteger(numericValue) && numericValue >= 0 && numericValue < dailyAxisValues.length
            ? dailyAxisValues[numericValue]
            : numericValue;
          return Number.isFinite(timestamp) ? axisFormatter(timestamp) : valueStr;
        },
      }] : [],
      series: chart.series.map((series, index): BarSeriesOption | LineSeriesOption => {
        const color = seriesColor(series.groups, index, useVehicleClassColors);
        const markLine: MarkLineComponentOption | undefined = !dailyAxis && index === 0 && dayMarkers.boundaries.length ? {
          silent: true,
          symbol: ["none", "none"],
          label: { show: false },
          lineStyle: { color: "#cfd2d6", width: 1.25, type: "solid" },
          data: dayMarkers.boundaries.map((marker) => ({ xAxis: marker.value, label: { show: false } })),
        } : undefined;
        const common = {
          name: groupLabel(series.groups, useVehicleClassColors, dataset),
          data: dailyAxis ? series.points.map((point) => point.value) : series.points.map((point) => [point.timestamp, point.value]),
          itemStyle: { color, opacity: kind === "bar" ? 0.82 : 1 },
          markLine,
        };
        return kind === "bar" ? {
          ...common,
          type: "bar",
          stack: "dashboard-total",
          barMaxWidth: 38,
        } : {
          ...common,
          type: "line",
          connectNulls: false,
          symbol: "none",
          showSymbol: false,
          lineStyle: { color, width: 2 },
        };
      }),
    };
  }, [axisFormatter, chart, dailyAxis, dailyAxisValues, dayMarkers.boundaries, dataset, kind, panel.format, showZoomSlider, timeZone, unit, useVehicleClassColors, valueScale]);

  useEffect(() => {
    if (!panel.id) return;
    return registerPanelChartExporter(panel.id, (size) => reportChartSVG(option, size));
  }, [option, panel.id]);

  return <>
    <div className="dynamic-chart-readout">
      <div>
        <strong>{chart.leadValue === null ? "–" : valueLabel(chart.leadValue, panel.format, unit)}</strong>
        <span>{kind === "bar" ? "Summe je Messinterval" : "Letztes Messintervall"} · {chart.leadTimestamp === null ? "–" : currentFormatter.format(chart.leadTimestamp)}</span>
      </div>
      {showZoomSlider && zoomed && <button type="button" className="text-action dynamic-chart-zoom-reset" onClick={() => chartCanvas.current?.resetZoom()}>Zoom zurücksetzen</button>}
    </div>
    <div
      className={`dynamic-line-chart measurement-echart${kind === "bar" ? " dynamic-bar-chart" : ""}${showZoomSlider ? " has-zoom-slider" : ""}`}
      data-chart-kind={kind}
      data-day-boundary-count={dayMarkers.boundaries.length}
      data-gap-count={gapCount}
      data-point-count={bucketCount}
      data-series-colors={chart.series.map((series, index) => seriesColor(series.groups, index, useVehicleClassColors)).join(",")}
      data-value-ticks={valueScale.ticks.map((tick) => tick.value).join(",")}
      data-zero-value-count={zeroValueCount}
      data-axis-pointer="cross"
      data-tooltip-total="true"
      data-x-axis-labels="true"
      data-x-axis-type={dailyAxis ? "category" : "time"}
      data-x-axis-days={dailyAxisValues.map((timestamp) => axisFormatter(timestamp)).join("|")}
      data-y-axis-labels="true"
      data-zoom-toolbox="false"
      data-zoom-slider={showZoomSlider ? "true" : "false"}
      data-zoom-slider-configured={zoomConfigured ? "true" : "false"}
    >
      <EChartsCanvas ref={chartCanvas} label={panel.title} option={option} zoomEnabled={showZoomSlider} onZoomChange={setZoomed} />
    </div>
  </>;
}
